import type { LinkOptional, LinkTypes } from '@@/shared-components/types/Link'
import {
  ARTICLE_ABOUT_US,
  ARTICLE_CONTACTS,
  ARTICLE_HOME,
  ARTICLE_SHIPPING,
  ARTICLE_TERMS,
  ARTICLE_COMPLAINT,
  ARTICLE_EU,
  ARTICLE_CAREER,
  PROFILE_ORDERS,
  PROFILE_ADDRESSES,
  PROFILE_LOYALTY,
  PROFILE_REMINDERS,
  PROFILE_EDIT,
  ARTICLE_RATING,
  ARTICLE_RATING_BAD,
  PROFILE_REGISTER,
  ARTICLE_PRIVACY,
} from '@@/shared-components/constants/articles'
import { ARTICLE_DETAIL_KEY, Redirect } from '@@/shared-components/redirect/settings'
import type { LocationQuery } from 'vue-router'
export const redirect = (
  type: LinkTypes,
  linkData: { id?: string; slug?: string; key: { name: string; value: string } | null },
  link?: string,
  query?: LocationQuery,
  allData?: object
) => {
  const { $localePath } = useNuxtApp()
  const router = useRouter()
  const RedirectInstance = Redirect.getInstance()
  const { id, slug, key, ...more } = linkData
  switch (type) {
    case 'anchor':
      return RedirectInstance.redirectParser(`#${link}`, allData)
    case 'article':
      if (linkData.id && linkData.slug) {
        return RedirectInstance.redirectParser(
          getPermanentRoute(
            linkData,
            router.resolve($localePath({ name: ARTICLE_DETAIL_KEY, params: { id, slug }, query }))
              ?.fullPath
          ),
          allData
        )
      }
      break
    case 'article-list':
      return RedirectInstance.redirectParser(
        getPermanentRoute(
          linkData,
          router.resolve($localePath({ name: 'article-list', query }))?.fullPath
        ),
        allData
      )
    case 'external':
      return link
    case 'login':
      return RedirectInstance.redirectParser(
        getPermanentRoute(
          linkData,
          router.resolve($localePath({ name: 'login', query }))?.fullPath
        ),
        allData
      )
    case 'logout':
      return RedirectInstance.redirectParser(
        getPermanentRoute(
          linkData,
          router.resolve($localePath({ name: 'logout', query }))?.fullPath
        ),
        allData
      )
    case 'reset-password':
      return RedirectInstance.redirectParser(
        getPermanentRoute(
          linkData,
          router.resolve($localePath({ name: 'reset-password', query }))?.fullPath
        ),
        allData
      )
    case 'branch':
      return RedirectInstance.redirectParser(
        getPermanentRoute(
          linkData,
          router.resolve($localePath({ name: 'branch-detail', params: { id, slug }, query }))
            ?.fullPath
        ),
        allData
      )
    case 'branch-list':
      return RedirectInstance.redirectParser(
        getPermanentRoute(
          linkData,
          router.resolve($localePath({ name: 'contact-us', query }))?.fullPath
        ),
        allData
      )
    case 'product':
      if (linkData.id && linkData.slug) {
        return RedirectInstance.redirectParser(
          getPermanentRoute(
            linkData,
            router.resolve($localePath({ name: 'product-detail', params: { id, slug }, query }))
              ?.fullPath
          ),
          allData
        )
      }
      break
    case 'product-list':
      return RedirectInstance.redirectParser(
        getPermanentRoute(
          linkData,
          router.resolve($localePath({ name: 'product-list', query }))?.fullPath
        ),
        allData
      )
    case 'register':
      return RedirectInstance.redirectParser(
        getPermanentRoute(
          linkData,
          router.resolve($localePath({ name: 'register', query }))?.fullPath
        ),
        allData
      )
    case 'seo-city':
      return RedirectInstance.redirectParser(
        getPermanentRoute(
          linkData,
          router.resolve($localePath({ name: 'cities-id', params: { slug, id }, query }))?.fullPath
        ),
        allData
      )
    case 'section':
      return RedirectInstance.redirectParser(
        getPermanentRoute(
          linkData,
          router.resolve($localePath({ name: 'section-detail', params: { id, slug }, query }))
            ?.fullPath
        ),
        allData
      )
    case 'home':
      return RedirectInstance.redirectParser('/', allData)
    default: {
      const extendedRoute = RedirectInstance.getExtendedRedirectRoute(type)
      const pageName = extendedRoute?.pageName
      // params z extend definice (např. product-list-* → category-detail s kódem kategorie `c`)
      const extendedParams = extendedRoute?.params?.(query ?? {}) ?? {}
      return RedirectInstance.redirectParser(
        getPermanentRoute(
          linkData,
          router.resolve(
            $localePath({
              name: typeof pageName === 'function' ? pageName(query) : pageName,
              params: { id, slug, ...more, ...extendedParams },
              query,
            })
          )?.fullPath
        ),
        allData
      )
    }
  }
}

export const resolveLinkUrl = async ({
  action,
  params,
}: {
  action: string
  params?: Record<string, unknown>
}) => {
  const { get } = useApi()
  const res = await get<{ url: string }>('/links/resolve', { action, ...params })
  return res.data.url
}

export const resolveLink = async ({
  action,
  params,
  onLocal,
}: {
  action: string
  params: Record<string, unknown>
  onLocal: (url: string) => void | Promise<void>
}) => {
  const requestURL = useRequestURL()
  const url = await resolveLinkUrl({ action, params })
  const resolvedURL = new URL(url, requestURL.origin)
  if (resolvedURL.host !== requestURL.host) {
    window.open(resolvedURL.href)
  } else {
    await onLocal(resolvedURL.href)
  }
}

export const resolvePernamentRoute = (key: string, route?: string) => {
  const router = useRouter()
  const { $localePath } = useNuxtApp()
  const auth = useAuth()
  const RedirectInstance = Redirect.getInstance()

  switch (key) {
    case ARTICLE_CONTACTS:
      return router.resolve($localePath({ name: 'contact-us' }))?.path
    case ARTICLE_ABOUT_US:
      return router.resolve($localePath({ name: 'about-us' }))?.path
    case ARTICLE_SHIPPING:
      return router.resolve($localePath({ name: 'prices-of-transport' }))?.path
    case ARTICLE_HOME:
      return router.resolve($localePath({ name: 'home' }))?.path
    case ARTICLE_TERMS:
      return router.resolve($localePath({ name: 'terms-and-conditions' }))?.path
    case ARTICLE_PRIVACY:
      return router.resolve($localePath({ name: 'privacy-policy' }))?.path
    case ARTICLE_COMPLAINT:
      return router.resolve($localePath({ name: 'complaints-policy' }))?.path
    case ARTICLE_EU:
      return router.resolve($localePath({ name: 'digital-transformation' }))?.path
    case ARTICLE_CAREER:
      return router.resolve($localePath({ name: 'career' }))?.path
    //Profile
    case PROFILE_ORDERS:
      return auth.value.user
        ? router.resolve($localePath({ name: 'profile-order-history' }))?.path
        : route
    case PROFILE_ADDRESSES:
      return auth.value.user
        ? router.resolve($localePath({ name: 'profile-shipping-addresses' }))?.path
        : route
    case PROFILE_LOYALTY:
      return auth.value.user
        ? router.resolve($localePath({ name: 'profile-loyalty-program' }))?.path
        : route
    case PROFILE_REMINDERS:
      return auth.value.user
        ? router.resolve($localePath({ name: 'profile-reminders' }))?.path
        : route
    case PROFILE_EDIT:
      return auth.value.user ? router.resolve($localePath({ name: 'profile-edit' }))?.path : route
    case ARTICLE_RATING:
      return router.resolve($localePath({ name: 'satisfaction-survey' }))?.path
    case ARTICLE_RATING_BAD:
      return router.resolve(
        $localePath({ name: 'satisfaction-survey', query: { rateNegative: 1 } })
      )?.path
    case PROFILE_REGISTER:
      return router.resolve($localePath({ name: 'register' }))?.path
    default: {
      const extendedPernamentRoute = RedirectInstance.getExtendedPernamentRoute(key)
      return extendedPernamentRoute ?? route
    }
  }
}

export const getPermanentRoute = (data: LinkOptional, route?: string) => {
  if (!data.key) {
    return route
  }
  return resolvePernamentRoute(data.key?.value, route)
}
